import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, computed, makeObservable, observable, reaction } from "mobx";
import { Maybe } from "@/domains/common/types";
import { MountedStore } from "@/store/pages/MountedStore";
import { SessionObservable } from "@/store/sessions/SessionObservable";

export class SessionsViewPageStore extends AppSubStore {
  tabId: string;
  sessionId: string;

  mountState = new MountedStore();
  itemObservable: Maybe<SessionObservable>;

  constructor(injectedDeps: AppSubStoreArgs & { tabId: string; sessionId: string }) {
    super(injectedDeps);

    this.tabId = injectedDeps.tabId;
    this.sessionId = injectedDeps.sessionId;

    makeObservable(this, {
      tabId: observable,
      sessionId: observable,
      setSessionId: action,
      mountState: false,
      useEffects: false,
      amIMounted: computed,
      itemObservable: observable,
      session: computed,
      itemExists: computed,
      showNotFound: computed,
      setSessionObservable: action,
    });

    reaction(
      () => ({ session: this.session, amIMounted: this.amIMounted }),
      async ({ session: maybeSession }) => {
        if (maybeSession) {
          this.setSessionObservable(maybeSession);
        }
      }
    );
  }

  setSessionId(sessionId: string) {
    this.sessionId = sessionId;
  }

  setSessionObservable(sessionObservable?: SessionObservable) {
    this.itemObservable = sessionObservable;
  }

  get amIMounted() {
    return this.mountState.isMounted;
  }

  get session() {
    if (!this.mountState.isMounted || !this.sessionId) return;
    return this.store.sessions.get(this.sessionId);
  }

  get showNotFound() {
    if (!this.mountState.isMounted) return false;

    return !this.itemExists;
  }

  get itemExists(): boolean {
    // Return true when it's unknown.
    if (!this.mountState.isMounted || !this.sessionId) {
      return true;
    }

    const doesNotExist = this.store.sessions.doesNotExist(this.sessionId);
    return doesNotExist !== true;
  }

  useEffects() {
    this.mountState.useEffects();
  }
}
